import React, { createContext, useContext, useMemo } from 'react';

const ThemeContext = createContext(null);

// Theme configurations matching the 4 themes from SiteCustomization
const THEMES = {
  MODERN: {
    id: 'MODERN',
    name: 'Modern',
    colors: {
      primary: '#3b82f6',
      primaryHover: '#2563eb',
      primaryLight: '#eff6ff',
      secondary: '#64748b',
      secondaryHover: '#475569',
      accent: '#f59e0b',
      background: '#ffffff',
      backgroundAlt: '#f8fafc',
      text: '#111827',
      textMuted: '#6b7280',
      border: '#e2e8f0',
      success: '#10b981',
      error: '#ef4444',
      warning: '#f59e0b'
    },
    fonts: {
      primary: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      heading: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
    },
    spacing: {
      xs: '0.25rem',
      sm: '0.5rem',
      md: '1rem',
      lg: '1.5rem',
      xl: '2rem',
      xxl: '3rem'
    },
    borderRadius: {
      sm: '0.25rem',
      md: '0.5rem',
      lg: '0.75rem',
      xl: '1rem',
      full: '9999px'
    }
  },
  ELEGANT: {
    id: 'ELEGANT',
    name: 'Elegant',
    colors: {
      primary: '#7c3aed',
      primaryHover: '#6d28d9',
      primaryLight: '#faf5ff',
      secondary: '#64748b',
      secondaryHover: '#475569',
      accent: '#a855f7',
      background: '#ffffff',
      backgroundAlt: '#f8fafc',
      text: '#1f2937',
      textMuted: '#6b7280',
      border: '#e2e8f0',
      success: '#10b981',
      error: '#ef4444',
      warning: '#f59e0b'
    },
    fonts: {
      primary: 'Georgia, "Times New Roman", serif',
      heading: 'Georgia, "Times New Roman", serif'
    },
    spacing: {
      xs: '0.25rem',
      sm: '0.5rem',
      md: '1rem',
      lg: '1.5rem',
      xl: '2rem',
      xxl: '3rem'
    },
    borderRadius: {
      sm: '0.125rem',
      md: '0.25rem',
      lg: '0.5rem',
      xl: '0.75rem',
      full: '9999px'
    }
  },
  MINIMAL: {
    id: 'MINIMAL',
    name: 'Minimal',
    colors: {
      primary: '#059669',
      primaryHover: '#047857',
      primaryLight: '#f0fdf4',
      secondary: '#64748b',
      secondaryHover: '#475569',
      accent: '#10b981',
      background: '#ffffff',
      backgroundAlt: '#f8fafc',
      text: '#0f172a',
      textMuted: '#64748b',
      border: '#e2e8f0',
      success: '#10b981',
      error: '#ef4444',
      warning: '#f59e0b'
    },
    fonts: {
      primary: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      heading: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    },
    spacing: {
      xs: '0.25rem',
      sm: '0.5rem',
      md: '1rem',
      lg: '1.5rem',
      xl: '2rem',
      xxl: '3rem'
    },
    borderRadius: {
      sm: '0.125rem',
      md: '0.25rem',
      lg: '0.375rem',
      xl: '0.5rem',
      full: '9999px'
    }
  },
  CLASSIC: {
    id: 'CLASSIC',
    name: 'Classic',
    colors: {
      primary: '#dc2626',
      primaryHover: '#b91c1c',
      primaryLight: '#fef2f2',
      secondary: '#57534e',
      secondaryHover: '#44403c',
      accent: '#f59e0b',
      background: '#ffffff',
      backgroundAlt: '#f8fafc',
      text: '#1c1917',
      textMuted: '#78716c',
      border: '#e2e8f0',
      success: '#10b981',
      error: '#ef4444',
      warning: '#f59e0b'
    },
    fonts: {
      primary: '"Merriweather", Georgia, serif',
      heading: '"Playfair Display", Georgia, serif'
    },
    spacing: {
      xs: '0.25rem',
      sm: '0.5rem',
      md: '1rem',
      lg: '1.5rem',
      xl: '2rem',
      xxl: '3rem'
    },
    borderRadius: {
      sm: '0.25rem',
      md: '0.375rem',
      lg: '0.5rem',
      xl: '0.75rem',
      full: '9999px'
    }
  }
};

export const ThemeProvider = ({ children, themeName = 'MODERN', customColors = {} }) => {
  const theme = useMemo(() => {
    const baseTheme = THEMES[themeName] || THEMES.MODERN;

    // Allow custom overrides for colors and other theme properties
    if (customColors && Object.keys(customColors).length > 0) {
      const customizedTheme = { ...baseTheme };

      // Override colors if provided
      if (customColors.primaryColor || customColors.secondaryColor || customColors.accentColor) {
        customizedTheme.colors = {
          ...baseTheme.colors,
          ...(customColors.primaryColor && { primary: customColors.primaryColor }),
          ...(customColors.secondaryColor && { secondary: customColors.secondaryColor }),
          ...(customColors.accentColor && { accent: customColors.accentColor })
        };
      }

      // Override fonts if provided
      if (customColors.headingFont || customColors.bodyFont) {
        customizedTheme.fonts = {
          ...baseTheme.fonts,
          ...(customColors.headingFont && { heading: customColors.headingFont }),
          ...(customColors.bodyFont && { primary: customColors.bodyFont })
        };
      }

      // Override button styles if provided
      if (customColors.buttonBorderRadius || customColors.buttonPadding) {
        customizedTheme.buttonStyles = {
          borderRadius: customColors.buttonBorderRadius || baseTheme.borderRadius.md,
          padding: customColors.buttonPadding || '0.625rem 1.25rem'
        };
      } else {
        customizedTheme.buttonStyles = {
          borderRadius: baseTheme.borderRadius.md,
          padding: '0.625rem 1.25rem'
        };
      }

      return customizedTheme;
    }

    // Add default button styles
    return {
      ...baseTheme,
      buttonStyles: {
        borderRadius: baseTheme.borderRadius.md,
        padding: '0.625rem 1.25rem'
      }
    };
  }, [themeName, customColors]);

  return (
    <ThemeContext.Provider value={theme}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export default ThemeContext;
